/**
 * Types we deliberately don't normalize, for one of three reasons:
 *  - No own `_id`: embedded value objects graphcache can't key. Embed them on
 *    the parent instead of warning about an unkeyable selection set.
 *  - Non-unique `_id`: the backend reuses an `_id` across logically distinct
 *    objects (draft vs live config, per-filter result rows), so normalizing
 *    would let one overwrite another.
 *  - Locally-managed lists: normalizing a server-owned collection clobbered a
 *    component that owns its own list state (see `Notifications` below).
 */
export const keys = {
  // No own `_id`.
  StudyAvailability: () => null,
  StudyAvailabilityDateRange: () => null,
  StudyAvailabilityWeekday: () => null,
  StudyAvailabilityOverride: () => null,
  StudyAvailabilityWindow: () => null,
  StudySlot: () => null,
  BookingConfig: () => null,
  BookingStartEndSessions: () => null,
  OnlineTaskBookingConfig: () => null,
  BookingSteps: () => null,
  UserMetaIdentityType: () => null,
  UserMetaType: () => null,
  BookingRating: () => null,
  BookingSubmissionRating: () => null,
  InvoiceTransactionsType: () => null,
  FocusGroupSessionSummary: () => null,
  FocusGroupSessionsUpdate: () => null,
  GoogleViewportType: () => null,
  AvailableIntegration: () => null,
  BookingModerator: () => null,
  BookingObserver: () => null,
  ModeratorCalendar: () => null,
  TeamCapabilities: () => null,
  TeamCapability: () => null,
  // Draft and live configs share one `_id`, so live would overwrite draft.
  StudyWelcomeBlock: () => null,
  StudyThankYouBlock: () => null,
  StudyFigmaPrototypeTaskBlock: () => null,
  // Result rows reuse `_id` across rows / filter states: cache the query result
  // but not these nested objects, else filter changes serve stale data.
  CardSortAggregateResultCard: () => null,
  CardSortAggregateResultCardCategory: () => null,
  BookingTaskResultDetailsCardSortCategory: () => null,
  MultipleChoiceQuestionResult: () => null,
  MultipleChoiceQuestionResultAnswer: () => null,
  MultipleChoiceQuestionResultOtherAnswer: () => null,
  TreeTestResult: () => null,
  VariantComparisonResult: () => null,
  OpinionScaleResult: () => null,
  UserNodePath: () => null,
  BookingTaskParticipantsCollection: () => null,
  FindingsResponse: () => null,
  GenerateFindingsResponse: () => null,
  ProcessingStatusCounts: () => null,
  // A translated transcript copies the `_id` of each source utterance, and a clipped transcript
  // keeps the `_id` of the full one, so normalizing would show one locale or clip in place of another.
  AskableVideoTranscriptType: () => null,
  AskableVideoTranscriptUtteranceType: () => null,
  // A "this block" rename gives some utterances of a speaker a new label but not others, so one
  // speaker `_id` can carry two labels until the transcript refetches.
  AskableVideoTranscriptSpeakerType: () => null,
  AskableVideoTranscriptUtteranceSpeakerType: () => null,
  // Builder task blocks and their value objects have no own `_id`. Children that
  // do carry `_id` (options, statements, items, cards, nodes) stay normalized.
  TaskBlockRecordingOptions: () => null,
  ConditionalLogicCondition: () => null,
  ConditionalLogicNextBlockContinue: () => null,
  ConditionalLogicNextBlockEnd: () => null,
  ConditionalLogicFigmaPrototype: () => null,
  ConditionalLogicMultipleChoiceQuestion: () => null,
  ConditionalLogicOpinionScale: () => null,
  ConditionalLogicStackRank: () => null,
  ConditionalLogicTreeTest: () => null,
  ConditionalLogicVariantComparison: () => null,
  MultipleChoiceQuestion: () => null,
  OpenAnswer: () => null,
  OpinionScale: () => null,
  Matrix: () => null,
  SystemUsabilityScale: () => null,
  StackRank: () => null,
  CardSort: () => null,
  CardSortCardOptions: () => null,
  CardSortCategoryOptions: () => null,
  TreeTest: () => null,
  LiveWebsiteTest: () => null,
  LiveMobileTest: () => null,
  FigmaPrototypeFile: () => null,
  VariantComparison: () => null,
  VariantComparisonOptionsExclusive: () => null,
  VariantComparisonOptionsAlternating: () => null,
  // A screener's branching belongs to the question it is saved on. The logic and its targets have
  // no own `_id`, and a rule's is not unique: every duplicate path copied rules verbatim until the
  // ids were regenerated, so one rule `_id` names a different rule — with different `option_ids` —
  // in each copy of a study. Normalizing let whichever study was cached last answer for all of
  // them, which is how a copied rule ended up matching answers that were not on its question.
  BookingRecruitScreenerLogic: () => null,
  BookingRecruitScreenerLogicRule: () => null,
  BookingRecruitScreenerLogicTarget: () => null,
  // markAllNotificationsAsReadByTeam returns the whole team's notifications;
  // normalizing that froze the dropdown, which manages its own list locally.
  Notifications: () => null,
};
